/**
 * useUploadFileV2 Hook
 *
 * PURPOSE:
 * Advanced file upload hook with concurrent upload management, thumbnail generation,
 * progress tracking, and cancellation support for Drive module.
 *
 * KEY FEATURES:
 * - Concurrent upload limiting (max 5 simultaneous uploads)
 * - Automatic thumbnail generation for images and videos
 * - Real-time progress tracking per file
 * - Cancellable uploads with proper cleanup
 * - Auto-removal of completed uploads from progress after 5 seconds
 * - Presigned URL generation for secure S3 uploads
 * - Fallback thumbnail job creation for server-side processing
 *
 * ARCHITECTURE:
 * 1. File Processing Pipeline:
 *    - Receives file with prefix and public flag
 *    - Generates thumbnail for media files (images/videos)
 *    - Obtains presigned URL from backend
 *    - Uploads file to S3 with progress tracking
 *    - Uploads thumbnail or creates server job
 *    - Returns attachment metadata
 *
 * 2. Concurrency Management:
 *    - Uses limiter to cap simultaneous uploads at MAX_CONCURRENT_UPLOADS (5)
 *    - Queues additional uploads automatically
 *    - Tracks active uploads via activeUploadsRef
 *
 * 3. Progress Tracking:
 *    - Maintains percent state with file name as key
 *    - Updates progress via axios onUploadProgress callback
 *    - Auto-removes completed (100%) uploads after REMOVE_DURATION (5s)
 *    - Provides optional onProgress callback for external monitoring
 *
 * 4. Cancellation Support:
 *    - Stores CancelTokenSource for each upload
 *    - Allows aborting specific uploads by file name
 *    - Cleans up progress state on cancellation
 *
 * THUMBNAIL HANDLING:
 * - Client-side generation for supported media types
 * - Falls back to server-side job when client generation fails
 * - Uploads to dedicated thumbnails prefix with public access
 * - Links thumbnail to main file via x-amz-meta-thumbnail header
 *
 * ERROR HANDLING:
 * - Distinguishes between cancellation and actual errors
 * - Non-blocking thumbnail failures (warns but continues)
 * - Proper cleanup of refs and timeouts on errors
 *
 * RETURN VALUES:
 * - uploadFiles: Main upload function accepting FileToUpload array
 * - abortUpload: Cancel specific upload by file name
 * - percent: Current upload progress for all files
 * - isUploading: Boolean indicating active uploads
 *
 * USAGE EXAMPLE:
 * const { uploadFiles, abortUpload, percent, isUploading } = useUploadFileV2({
 *   onProgress: (progress) => console.log('Upload progress:', progress)
 * });
 *
 * const results = await uploadFiles([
 *   { uploadedFile: file, prefix: 'documents', isPublic: true }
 * ]);
 */

import { Attachment } from '@/modules/Upload/types'
import axios, { CancelTokenSource } from 'axios'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createConcurrencyLimiter } from '@/lib/utils'
import { presignedUrl } from '@/modules/Upload/services'
import { t } from 'i18next'
import datacentral from '@/utils/axios/datacentral'
import { getThumbnailFile } from '@/modules/Drive/utils/ThumbnailGenerator'

const MAX_CONCURRENT_UPLOADS = 5
const REMOVE_DURATION = 5000 // ms

export interface FileToUpload {
    uploadedFile: File
    prefix: string
    isPublic?: boolean
}

interface UseUploadFileV2Props {
    onProgress?: (percent: Record<string, number>) => void
}

const useUploadFileV2 = ({ onProgress }: UseUploadFileV2Props = {}) => {
    // Store cancel tokens for each file upload
    const cancelTokensRef = useRef<Record<string, CancelTokenSource>>({})

    // Store percent for each file upload
    const [percent, setPercent] = useState<Record<string, number>>({})

    // Add this useRef to track which files have a timeout set
    const completedTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({})

    // Track active uploads
    const activeUploadsRef = useRef<Set<string>>(new Set())

    const limiter = useMemo(
        () =>
            createConcurrencyLimiter(MAX_CONCURRENT_UPLOADS)<
                Partial<Attachment>
            >,
        []
    )

    const handleUploadThumbnail = useCallback(
        async (
            thumbnail: File | null,
            thumbnailKey: string,
            objectKey: string
        ): Promise<void> => {
            if (!thumbnail) {
                const response = await datacentral.post(
                    `/drive/thumbnail/job`,
                    {
                        object_key: objectKey,
                        output_key: thumbnailKey,
                    }
                )
                if (!response.data?.result) {
                    throw new Error('Failed to create thumbnail job')
                }
                return
            }

            const response = await presignedUrl({
                prefix: `thumbnails`,
                key: thumbnailKey,
                isPublic: true,
            })

            if (!response?.result?.url) {
                throw new Error('Failed to get presigned url for thumbnail')
            }

            await axios.put(response.result.url, thumbnail, {
                headers: { 'Content-Type': thumbnail.type },
                validateStatus: (status) => status >= 200 && status < 300,
            })
        },
        []
    )

    const uploadFile = useCallback(
        async (
            file: FileToUpload,
            thumbnailFile: File | null,
            thumbnailKey: string
        ): Promise<Partial<Attachment>> => {
            const presignedResponse = await presignedUrl({
                prefix: file.prefix,
                key: file.uploadedFile.name,
                isPublic: file.isPublic ?? true,
            })

            if (!presignedResponse?.result?.url) {
                throw new Error('Failed to get presigned url')
            }

            const cancelTokenSource = axios.CancelToken.source()
            cancelTokensRef.current[file.uploadedFile.name] = cancelTokenSource

            try {
                const uploadResponse = await axios.put(
                    presignedResponse?.result?.url,
                    file.uploadedFile,
                    {
                        onUploadProgress: (p) => {
                            const percent = Math.floor(
                                (p.loaded * 100) / (p.total || 1)
                            )
                            setPercent((prev) => ({
                                ...prev,
                                [file.uploadedFile.name]: percent,
                            }))
                        },
                        headers: {
                            'Content-Type': file.uploadedFile.type,
                            ...(thumbnailKey && {
                                'x-amz-meta-thumbnail': thumbnailKey,
                            }),
                        },
                        cancelToken: cancelTokenSource.token,
                    }
                )
                if (uploadResponse.status !== 200) {
                    throw new Error('Failed to upload file')
                }

                // Upload thumbnail asynchronously (non-blocking)
                const objectKey = ['pub', file.prefix, file.uploadedFile.name]
                    .filter(Boolean)
                    .join('/')
                    .replace(/\/{2,}/g, '/')

                try {
                    await handleUploadThumbnail(
                        thumbnailFile,
                        thumbnailKey,
                        objectKey
                    )
                } catch (err) {
                    console.warn(
                        `Thumbnail upload failed for ${file.uploadedFile.name}:`,
                        err
                    )
                }

                return {
                    attachment_file_url: [
                        file.prefix,
                        encodeURIComponent(file.uploadedFile.name),
                    ]
                        .join('/')
                        .replace(/\/{2,}/g, '/'),
                    attachment_file_size: file.uploadedFile.size,
                    attachment_file_name: file.uploadedFile.name,
                    attachment_file_type: file.uploadedFile.type,
                }
            } catch (err) {
                if (axios.isCancel(err)) {
                    setPercent((prev) => {
                        const updated = { ...prev }
                        delete updated[file.uploadedFile.name]
                        return updated
                    })
                    throw new Error(
                        `Upload cancelled: ${file.uploadedFile.name}`
                    )
                }
                throw new Error(t('upload-file-failed'))
            } finally {
                delete cancelTokensRef.current[file.uploadedFile.name]
            }
        },
        [handleUploadThumbnail]
    )

    // Update progress callback when percent changes
    useEffect(() => {
        onProgress?.(percent)
    }, [percent, onProgress])

    const processFile = useCallback(
        async (file: FileToUpload): Promise<Partial<Attachment>> => {
            let thumbnailKey = ''
            let thumbnailFile: File | null = null

            // Mark file as active
            activeUploadsRef.current.add(file.uploadedFile.name)

            try {
                // Check file is image/video or office doc
                if (
                    (file.uploadedFile.type.startsWith('image/') ||
                        file.uploadedFile.type.startsWith('video/')) &&
                    file.uploadedFile.type !== 'image/heic'
                ) {
                    // Generate thumbnail
                    const thumbnail = await getThumbnailFile(file.uploadedFile)
                    if (thumbnail) thumbnailFile = thumbnail
                }
                thumbnailKey = thumbnailFile
                    ? thumbnailFile.name
                    : crypto.randomUUID()

                // Upload file through limiter (handles concurrency)
                const result = await limiter(() =>
                    uploadFile(file, thumbnailFile, thumbnailKey)
                )

                return result
            } finally {
                // Remove from active uploads
                activeUploadsRef.current.delete(file.uploadedFile.name)
            }
        },
        [limiter, uploadFile]
    )

    // Auto-remove completed uploads from progress after delay
    useEffect(() => {
        Object.entries(percent).forEach(([fileName, value]) => {
            if (value === 100 && !completedTimeoutsRef.current[fileName]) {
                completedTimeoutsRef.current[fileName] = setTimeout(() => {
                    setPercent((prev) => {
                        const updated = { ...prev }
                        delete updated[fileName]
                        return updated
                    })
                    delete completedTimeoutsRef.current[fileName]
                }, REMOVE_DURATION)
            }
        })

        // Cleanup orphaned timeouts
        Object.keys(completedTimeoutsRef.current).forEach((fileName) => {
            if (!(fileName in percent)) {
                clearTimeout(completedTimeoutsRef.current[fileName])
                delete completedTimeoutsRef.current[fileName]
            }
        })
    }, [percent])

    const uploadFiles = useCallback(
        async (
            files: FileToUpload[]
        ): Promise<PromiseSettledResult<Partial<Attachment>>[]> => {
            if (files.length === 0) return []
            return Promise.allSettled(files.map((file) => processFile(file)))
        },
        [processFile]
    )

    const abortUpload = useCallback((fileName: string): boolean => {
        const cancelToken = cancelTokensRef.current[fileName]
        if (cancelToken) {
            cancelToken.cancel(`Upload cancelled by user: ${fileName}`)
            activeUploadsRef.current.delete(fileName)
            return true
        }
        return false
    }, [])

    return {
        uploadFiles,
        abortUpload,
        percent,
        isUploading: activeUploadsRef.current.size > 0,
    }
}

export default useUploadFileV2
